/* eslint-disable */
import React from "react"
import { numberFormatter } from "../../../../../utils/numbers.utils"
import { getMonthLongNameByNumber } from "../../../../../utils/date.utils"
import { UNIFIEDDATE_VALUES } from "../../../../../consts/CompoundRangeDatePicker"

/**
 * @param {import("./TeamHoursConsumptionTable.types").DualHoursDisplayProps} props
 * @returns {import("react").ReactElement}
 */
function DualHoursDisplay({ billed, sisu }) {
  return (
    <span className="flex align-items-center gap-1">
      <span className="team-hours-consumption__primary-text">
        {numberFormatter(billed ?? 0)}
      </span>
      <span className="team-hours-consumption__sisu-secondary">
        {numberFormatter(sisu ?? 0)}
      </span>
    </span>
  )
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").SingleHoursDisplayProps} props
 * @returns {import("react").ReactElement}
 */
function SingleHoursDisplay({ value }) {
  return (
    <span className="flex align-items-center gap-1">
      <span className="team-hours-consumption__primary-text">
        {numberFormatter(value ?? 0)}
      </span>
    </span>
  )
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursRowData} data
 * @returns {number}
 */
const sumRangeSisu = (data) => {
  return Object.values(data.rangeData || {}).reduce((acc, cell) => {
    return acc + (Number(cell?.sisuTime) || 0)
  }, 0)
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursRowData} data
 * @returns {number}
 */
const sumRangeBilled = (data) => {
  return Object.values(data.rangeData || {}).reduce((acc, cell) => {
    return acc + (Number(cell?.billedTime) || 0)
  }, 0)
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursRowData} data
 * @returns {number}
 */
const rowSisuTotal = (data) => {
  if (data.sisuTime != null && data.sisuTime !== "") {
    return Number(data.sisuTime)
  }
  return sumRangeSisu(data)
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursRowData} data
 * @returns {number}
 */
const rowBilledTotal = (data) => {
  if (data.billedTime != null && data.billedTime !== "") {
    return Number(data.billedTime)
  }
  return sumRangeBilled(data)
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursRowData} data
 * @returns {number}
 */
const rowAverageSisu = (data) => {
  return data.averageSisuTime ?? data.averageTime ?? 0
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursRowData} data
 * @returns {number}
 */
const rowAverageBilled = (data) => {
  return data.averageBilledTime ?? 0
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").PeriodRangeColumnHeaderProps} props
 * @returns {string}
 */
function PeriodRangeColumnHeader({
  rangePeriod,
  isLastYear,
  startYear,
  endYear,
  period,
}) {
  if (period === UNIFIEDDATE_VALUES.RANGEMONTH) {
    return (
      (getMonthLongNameByNumber(rangePeriod)?.slice(0, 3) || "") +
      "/" +
      (isLastYear ? endYear.slice(2, 4) : startYear.slice(2, 4))
    )
  }
  if (period === UNIFIEDDATE_VALUES.RANGEWEEK) {
    return (
      String(rangePeriod) +
      "/" +
      (isLastYear ? endYear.slice(2, 4) : startYear.slice(2, 4))
    )
  }
  return String(rangePeriod)
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").PeriodRangeColumnBodyProps} props
 * @returns {import("react").ReactElement}
 */
function PeriodRangeColumnBody({ rowData, rangePeriod, showBilledHours }) {
  const cell = rowData.data.rangeData?.[`${rangePeriod}`] || {}
  const sisu = Number(cell.sisuTime) || 0
  const billed = Number(cell.billedTime) || 0
  if (showBilledHours) {
    return <DualHoursDisplay billed={billed} sisu={sisu} />
  }
  return <SingleHoursDisplay value={sisu} />
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").PeriodRangeColumnFooterProps} props
 * @returns {import("react").ReactNode}
 */
function PeriodRangeColumnFooter({
  totals,
  rangePeriod,
  period,
  showBilledHours,
}) {
  if (totals === null || totals === undefined) {
    return null
  }

  let bucket
  if (
    period === UNIFIEDDATE_VALUES.RANGEMONTH &&
    totals.monthData !== undefined
  ) {
    bucket = totals.monthData[rangePeriod]
  } else if (
    period === UNIFIEDDATE_VALUES.RANGEWEEK &&
    totals.weekData !== undefined
  ) {
    bucket = totals.weekData[rangePeriod]
  }
  if (!bucket) {
    return null
  }
  const sisu = Number(bucket.sisuTime) || 0
  const billed = Number(bucket.billedTime) || 0
  if (showBilledHours) {
    return <DualHoursDisplay billed={billed} sisu={sisu} />
  }
  return numberFormatter(sisu)
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").TotalsColumnHeaderProps} props
 * @returns {string}
 */
function TotalsColumnHeader({ showBilledHours }) {
  return showBilledHours ? "Hrs facturadas totales" : "Hrs consumidas totales"
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").TotalsColumnBodyProps} props
 * @returns {import("react").ReactElement}
 */
function TotalsColumnBody({ rowData, showBilledHours }) {
  const sisu = rowSisuTotal(rowData.data)
  const billed = rowBilledTotal(rowData.data)
  if (showBilledHours) {
    return <DualHoursDisplay billed={billed} sisu={sisu} />
  }
  return <SingleHoursDisplay value={sisu} />
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").TotalsColumnFooterProps} props
 * @returns {import("react").ReactNode}
 */
function TotalsColumnFooter({ totals, showBilledHours }) {
  const sisu = Number(totals?.sisuTime) || 0
  const billed = Number(totals?.billedTime) || 0
  if (showBilledHours) {
    return <DualHoursDisplay billed={billed} sisu={sisu} />
  }
  return numberFormatter(totals?.sisuTime ?? 0)
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").AverageColumnHeaderProps} props
 * @returns {import("react").ReactNode}
 */
function AverageColumnHeader({ showBilledHours }) {
  if (showBilledHours) {
    return (
      <span className="flex flex-column line-height-1">
        <span>Promedio de hrs</span>
        <span>facturadas</span>
      </span>
    )
  }
  return "Promedio de hrs consumidas"
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").AverageColumnBodyProps} props
 * @returns {import("react").ReactElement}
 */
function AverageColumnBody({ rowData, showBilledHours }) {
  const sisuAvg = rowAverageSisu(rowData.data)
  const billedAvg = rowAverageBilled(rowData.data)
  if (showBilledHours) {
    return <DualHoursDisplay billed={billedAvg} sisu={sisuAvg} />
  }
  return <SingleHoursDisplay value={sisuAvg} />
}

/**
 * @param {import("./TeamHoursConsumptionTable.types").AverageColumnFooterProps} props
 * @returns {import("react").ReactNode}
 */
function AverageColumnFooter({ totals, showBilledHours }) {
  const sisuAvg = totals?.averageSisuTime ?? totals?.averageTime ?? 0
  const billedAvg = totals?.averageBilledTime ?? 0
  if (showBilledHours) {
    return <DualHoursDisplay billed={billedAvg} sisu={sisuAvg} />
  }
  return numberFormatter(sisuAvg)
}

/**
 * Builds column definitions for the team hours consumption tree table.
 *
 * @param {(string|number)[]} rangeToConsider
 * @param {string} startYear
 * @param {string} endYear
 * @param {string} period
 * @param {import("./TeamHoursConsumptionTable.types").TeamHoursReportTotals|null|undefined} totals
 * @param {boolean} showBilledHours
 * @returns {object[]}
 */
const ColumnContent = (
  rangeToConsider,
  startYear,
  endYear,
  period,
  totals,
  showBilledHours
) => {
  return [
    ...rangeToConsider
      .reduce((acc, next) => {
        const lastItem = acc[acc.length - 1]?.[0]
        const lastItemState = acc[acc.length - 1]?.[1]
        if ((lastItem && next < lastItem) || lastItemState === true) {
          acc.push([next, true])
          return acc
        } else {
          acc.push([next, false])
          return acc
        }
      }, [])
      .map(([rangePeriod, isLastYear]) => {
        return {
          key: rangePeriod,
          field: `rangeData.${rangePeriod}.sisuTime`,
          header: () => {
            return (
              <PeriodRangeColumnHeader
                rangePeriod={rangePeriod}
                isLastYear={isLastYear}
                startYear={startYear}
                endYear={endYear}
                period={period}
              />
            )
          },
          style: { padding: "7px 7px 7px 0px" },
          body: (rowData) => {
            return (
              <PeriodRangeColumnBody
                rowData={rowData}
                rangePeriod={rangePeriod}
                showBilledHours={showBilledHours}
              />
            )
          },
          footer: () => {
            return (
              <PeriodRangeColumnFooter
                totals={totals}
                rangePeriod={rangePeriod}
                period={period}
                showBilledHours={showBilledHours}
              />
            )
          },
        }
      }),
    {
      body: (rowData) => {
        return (
          <TotalsColumnBody
            rowData={rowData}
            showBilledHours={showBilledHours}
          />
        )
      },
      style: { padding: "7px 7px 7px 0px" },
      field: "sisuTime",
      header: () => {
        return <TotalsColumnHeader showBilledHours={showBilledHours} />
      },
      footer: () => {
        return (
          <TotalsColumnFooter
            totals={totals}
            showBilledHours={showBilledHours}
          />
        )
      },
    },
    {
      body: (rowData) => {
        return (
          <AverageColumnBody
            rowData={rowData}
            showBilledHours={showBilledHours}
          />
        )
      },
      style: { padding: "7px 7px 7px 0px" },
      field: "averageSisuTime",
      header: () => {
        return <AverageColumnHeader showBilledHours={showBilledHours} />
      },
      footer: () => {
        return (
          <AverageColumnFooter
            totals={totals}
            showBilledHours={showBilledHours}
          />
        )
      },
    },
  ]
}

export default ColumnContent
